import { useEffect } from 'react';
import { ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { entityLabel } from '../../helpers/effectiveCapitalFormat';

export const ConnectAccountModal = ({
  open,
  onClose,
  entity,
  onEntityChange,
  depositoryOnly,
  onDepositoryChange,
  onContinue,
  disabled,
}) => {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="connect-account-title">
      <button type="button" className="absolute inset-0 bg-gray-900/40" onClick={onClose} aria-label="Close" />
      <div className="absolute left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between px-5 pt-5">
          <div>
            <h3 id="connect-account-title" className="text-base font-semibold text-gray-900">Connect Account</h3>
            <p className="mt-1 text-sm text-gray-500">Choose the entity this institution belongs to, then continue to Plaid.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-gray-500">Entity</label>
            <div className="grid grid-cols-2 gap-2">
              {['kings_loot', 'kings_holding'].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onEntityChange(value)}
                  className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium ${
                    entity === value
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {entityLabel(value)}
                </button>
              ))}
            </div>
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 rounded-md border border-gray-200 px-3 py-2.5 text-sm hover:bg-gray-50">
            <input
              type="checkbox"
              checked={depositoryOnly}
              onChange={(event) => onDepositoryChange(event.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            <span>
              <span className="font-medium text-gray-900">Checking / savings only</span>
              <span className="block text-xs text-gray-500">Skip liabilities consent. Use for institutions like Flex KH that return “No liability accounts”.</span>
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 items-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onContinue}
            disabled={disabled}
            className="flex h-9 items-center gap-1.5 rounded-md bg-gray-900 px-4 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            Continue to Plaid
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
